"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import type { GhostedPayload, GhostedTrack } from "@/app/lib/ghosted";
import { LoadingDots } from "./LoadingDots";

function formatTrackLabel(track: GhostedTrack | null) {
  if (!track) {
    return "Nothing playing right now";
  }
  return track.title ?? "Unknown track";
}

function formatTime(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function SpotifyCard() {
  const [data, setData] = useState<GhostedPayload | null>(null);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    let ignore = false;

    async function load() {
      try {
        const response = await fetch("/api/ghosted", { cache: "no-store" });
        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as GhostedPayload;
        if (!ignore) {
          setData(payload);
        }
      } catch {
      }
    }

    load();
    const interval = setInterval(load, 30_000);
    return () => {
      ignore = true;
      clearInterval(interval);
    };
  }, []);

  const currentTrack = data?.currentTrack ?? data?.lastPlayed ?? null;
  const isPlaying = data?.discord?.listeningToSpotify ?? false;

  useEffect(() => {
    if (!currentTrack?.startedAt || !currentTrack?.endedAt) {
      setProgress(0);
      setElapsed(0);
      setDuration(0);
      return;
    }

    const start = new Date(currentTrack.startedAt).getTime();
    const end = new Date(currentTrack.endedAt).getTime();
    const total = end - start;
    setDuration(total);

    function tick() {
      const now = Date.now();
      const el = Math.min(now - start, total);
      setElapsed(el);
      setProgress(total > 0 ? (el / total) * 100 : 0);
    }

    tick();

    if (!isPlaying) return;

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [currentTrack?.startedAt, currentTrack?.endedAt, isPlaying]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="mx-auto w-full max-w-sm"
    >
      <div className="rounded-lg bg-white/5 backdrop-blur-sm overflow-hidden p-4 transition-all duration-300 hover:bg-white/8">
        {!data ? (
          <LoadingDots />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 56,
                height: 56,
                minWidth: 56,
                borderRadius: 8,
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {currentTrack?.albumArtUrl ? (
                <img
                  src={currentTrack.albumArtUrl}
                  alt={currentTrack.album ?? "Album art"}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(189,189,189,0.5)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              )}
            </div>

            <div style={{ minWidth: 0, flex: 1 }}>
              <p className="text-[0.95rem] font-semibold text-foreground line-clamp-1">
                {formatTrackLabel(currentTrack)}
              </p>
              <p className="text-[0.75rem] text-muted/60 line-clamp-1" style={{ marginTop: 2 }}>
                {currentTrack?.artist ?? "Unknown artist"}
              </p>

              {currentTrack?.startedAt && currentTrack?.endedAt && (
                <div style={{ marginTop: 8 }}>
                  <div
                    style={{
                      width: '100%',
                      height: 3,
                      borderRadius: 2,
                      background: 'rgba(255,255,255,0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(progress, 100)}%`,
                        height: '100%',
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.4)',
                        transition: 'width 1s linear',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 4,
                    }}
                  >
                    <span className="text-[0.6rem] text-muted/40">{formatTime(elapsed)}</span>
                    <span className="text-[0.6rem] text-muted/40">{formatTime(duration)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
